import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import useTranslation from "@babackapp/translations/use-translation";
import { ClipboardCheck, ClipboardCopy } from "lucide-react";
import { PropsWithChildren, useState } from "react";
import Loading from "@/components-legacy/loading";

type CreatedKey = { token: string };

interface CreateApiKeyDialogProps extends PropsWithChildren {
  onSubmit: (label: string) => Promise<CreatedKey>;
}

export default function CreateApiKeyDialog({
  children,
  onSubmit,
}: CreateApiKeyDialogProps) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [hasCopied, setHasCopied] = useState(false);

  const _handleClose = () => {
    setOpen(false);
    setLabel("");
    setCreatedToken(null);
    setHasCopied(false);
  };

  const _handleSubmit = async () => {
    setIsPending(true);
    try {
      const key = await onSubmit(label);
      setCreatedToken(key.token);
    } finally {
      setIsPending(false);
    }
  };

  const _handleCopy = () => {
    if (!createdToken) return;
    void navigator.clipboard.writeText(createdToken);
    setHasCopied(true);
    setTimeout(() => setHasCopied(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) _handleClose();
        else setOpen(true);
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="pt-8">
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.api.createKey")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.api.apiKeyDescription")}
          </DialogDescription>
        </DialogHeader>

        {createdToken ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-xs text-muted-foreground">
                {t("settings.integrations.api.newKeyToken")}
              </Label>
              <button
                type="button"
                onClick={_handleCopy}
                className="flex items-center justify-between gap-2 rounded border bg-muted px-3 py-2 text-left font-mono text-sm cursor-pointer hover:bg-muted/70 transition-colors w-full"
              >
                <span className="break-all">{createdToken}</span>
                <span className="shrink-0 text-muted-foreground">
                  {hasCopied ? (
                    <ClipboardCheck className="size-4 text-green-500" />
                  ) : (
                    <ClipboardCopy className="size-4" />
                  )}
                </span>
              </button>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={_handleCopy}
                className="flex-1 gap-2"
                disabled={hasCopied}
              >
                {hasCopied ? (
                  <>
                    <ClipboardCheck className="size-4 text-green-500" />
                    {t("settings.integrations.api.copied")}
                  </>
                ) : (
                  <>
                    <ClipboardCopy className="size-4" />
                    {t("settings.integrations.api.copyToken")}
                  </>
                )}
              </Button>
              <Button onClick={_handleClose} className="flex-1">
                {t("misc.close")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="api-key-label">
                {t("settings.integrations.api.keyLabel")}
              </Label>
              <Input
                id="api-key-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t(
                  "settings.integrations.api.keyLabelPlaceholder",
                )}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void _handleSubmit();
                }}
              />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={_handleClose}>
                {t("misc.cancel")}
              </Button>
              <Button onClick={() => void _handleSubmit()} disabled={isPending}>
                {isPending ? (
                  <Loading className="w-4 h-4" />
                ) : (
                  t("settings.integrations.api.createKey")
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
